import { Icon } from "@iconify/react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { PlusIcon, SearchIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { type TicketPriority, TicketStatus, type TicketStatus as TicketStatusValue, useTicketApi } from "@/api/tickets";
import { Permissions } from "@/auth/permissions";
import { usePermission } from "@/auth/session-provider";
import { DataTableFacetedFilter } from "@/components/data-table/data-table-faceted-filter";
import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import { DataTableRefreshButton } from "@/components/data-table/data-table-refresh-button";
import { DataTableRequestBoundary } from "@/components/data-table/data-table-request-boundary";
import { DataTableSortList } from "@/components/data-table/data-table-sort-list";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { priorityPresentation, statusPresentation, TICKET_PRIORITY_VALUES } from "@/features/tickets/utils/ticket-presentation";
import { useI18n } from "@/locales/i18n-provider";
import { useEmbeddedHost } from "../embedded-host-provider";
import { EmbeddedTicketListTable } from "./ticket-list-table";

const PAGE_SIZE = 20;
const DEFAULT_SORTING: SortingState = [
  { id: "priority", desc: true },
  { id: "creationTime", desc: true },
];
const TABS = [
  { key: "all", labelKey: "embedded.tickets.tabs.all", countKey: "all" },
  { key: "assigned", labelKey: "embedded.tickets.tabs.assigned", countKey: "assignedToMe" },
  { key: "participating", labelKey: "embedded.tickets.tabs.participating", countKey: "participatingByMe" },
] as const;
const DEFAULT_TAB = TABS[1];
const SORT_OPTIONS = [
  { value: "creationTime", labelKey: "tickets.fields.creationTime", apiField: "CreationTime" },
  { value: "lastModificationTime", labelKey: "tickets.fields.lastUpdated", apiField: "LastModificationTime" },
  { value: "resolvedAt", labelKey: "tickets.fields.resolvedAt", apiField: "ResolvedAt" },
  { value: "priority", labelKey: "tickets.fields.priority", apiField: "Priority" },
  { value: "status", labelKey: "tickets.fields.status", apiField: "Status" },
  { value: "number", labelKey: "tickets.fields.number", apiField: "Number" },
] as const;
const SORT_FIELDS = new Map<string, string>(SORT_OPTIONS.map((option) => [option.value, option.apiField]));
const STATUS_VALUES: TicketStatusValue[] = [
  TicketStatus.New,
  TicketStatus.Open,
  TicketStatus.Pending,
  TicketStatus.Resolved,
  TicketStatus.Closed,
  TicketStatus.Cancelled,
];

function parseValues<T extends number>(value: string | null, allowed: ReadonlyArray<T>): T[] {
  return [...new Set((value ?? "").split(",").map(Number))].filter((item): item is T => allowed.includes(item as T));
}

function parseSorting(value: string | null): SortingState {
  if (!value) return DEFAULT_SORTING;
  const seen = new Set<string>();
  const parsed = value
    .split(",")
    .map((entry) => {
      const [id, direction] = entry.split(".");
      return { id: id ?? "", desc: direction !== "asc" };
    })
    .filter((entry) => SORT_FIELDS.has(entry.id) && !seen.has(entry.id) && seen.add(entry.id))
    .slice(0, 3);
  return parsed.length ? parsed : DEFAULT_SORTING;
}

/** 嵌入端保留自己的路由和查询状态，列表外观与完整工作台保持一致。 */
export default function EmbeddedTicketsPage() {
  const { intlLocale, t } = useI18n();
  const { can } = usePermission();
  const ticketApi = useTicketApi();
  const { projectCode } = useEmbeddedHost();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = TABS.find((tab) => tab.key === searchParams.get("view")) ?? DEFAULT_TAB;
  const requestedPage = Number(searchParams.get("page") ?? "1");
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const appliedSearch = searchParams.get("q") ?? "";
  const [search, setSearch] = useState(appliedSearch);
  const priorities = parseValues<TicketPriority>(searchParams.get("priority"), TICKET_PRIORITY_VALUES);
  const statuses = parseValues<TicketStatusValue>(searchParams.get("status"), STATUS_VALUES);
  const filters = { projectCode, filter: appliedSearch || undefined, priorities, statuses };
  const sorting = parseSorting(searchParams.get("sort"));
  const apiSorting = sorting.map((item) => `${SORT_FIELDS.get(item.id)} ${item.desc ? "DESC" : "ASC"}`).join(", ");

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(updates)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => setSearch(appliedSearch), [appliedSearch]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalized = search.trim();
      if (normalized !== appliedSearch) updateSearchParams({ q: normalized || null, page: null });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [appliedSearch, search, updateSearchParams]);

  const query = useQuery({
    queryKey: [
      "tickets",
      "list",
      "embedded-all",
      { projectCode, view: selectedTab.key, search: appliedSearch, priorities, statuses, page, sorting: apiSorting },
    ],
    queryFn: ({ signal }) =>
      ticketApi.getList(
        {
          ...filters,
          assignedToMe: selectedTab.key === "assigned" || undefined,
          participatingByMe: selectedTab.key === "participating" || undefined,
          skipCount: (page - 1) * PAGE_SIZE,
          maxResultCount: PAGE_SIZE,
          sorting: apiSorting,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });
  const countsQuery = useQuery({
    queryKey: ["tickets", "counts", "views", "embedded-all", filters],
    queryFn: ({ signal }) => ticketApi.getViewCounts(filters, signal),
    placeholderData: keepPreviousData,
  });
  const totalCount = query.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (!query.isPending && page > totalPages) updateSearchParams({ page: totalPages > 1 ? String(totalPages) : null });
  }, [page, query.isPending, totalPages, updateSearchParams]);

  const priorityOptions = [...TICKET_PRIORITY_VALUES]
    .sort((a, b) => b - a)
    .map((value) => ({
      value: String(value),
      label: t(priorityPresentation(value).labelKey),
      icon: priorityPresentation(value).icon,
    }));
  const statusOptions = STATUS_VALUES.map((value) => ({
    value: String(value),
    label: t(statusPresentation(value).labelKey),
    icon: statusPresentation(value).icon,
  }));
  const sortOptions = SORT_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }));
  const hasFilters = Boolean(search || priorities.length || statuses.length);
  const setSorting = (next: SortingState) => {
    const valid = next.filter((item, index) => SORT_FIELDS.has(item.id) && next.findIndex((entry) => entry.id === item.id) === index).slice(0, 3);
    const normalized = valid.length ? valid : DEFAULT_SORTING;
    const encoded = normalized.map((item) => `${item.id}.${item.desc ? "desc" : "asc"}`).join(",");
    updateSearchParams({ sort: encoded === "priority.desc,creationTime.desc" ? null : encoded, page: null });
  };

  return (
    <main className="flex h-full min-h-112 w-full flex-col gap-4 py-3 px-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={selectedTab.key} onValueChange={(next) => updateSearchParams({ view: next === DEFAULT_TAB.key ? null : next, page: null })}>
          <TabsList className="h-9 gap-2" variant="line">
            {TABS.map((tab) => {
              const count = countsQuery.data?.[tab.countKey];
              return (
                <TabsTrigger className="gap-2 px-1" key={tab.key} value={tab.key}>
                  {t(tab.labelKey)}
                  <Badge className="min-w-6 justify-center" variant="secondary">
                    {count === undefined ? "–" : new Intl.NumberFormat(intlLocale).format(count)}
                  </Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
        {projectCode && can(Permissions.tickets.create) ? (
          <Button asChild className="shrink-0 h-9">
            <Link to={{ pathname: "/embedded/tickets/new", search: location.search }} state={{ returnTo: `${location.pathname}${location.search}` }}>
              <PlusIcon data-icon="inline-start" />
              {t("tickets.new.trigger")}
            </Link>
          </Button>
        ) : null}
      </div>

      <DataTableToolbar
        actions={
          <>
            <DataTableSortList options={sortOptions} sorting={sorting} onSortingChange={setSorting} />
            <DataTableRefreshButton
              label={t("tickets.actions.refresh")}
              loadingLabel={t("tickets.list.refreshing")}
              refreshing={query.isFetching}
              onClick={() => query.refetch()}
            />
          </>
        }
      >
        <InputGroup className="h-9 min-w-0 flex-1 md:max-w-xs">
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
          <InputGroupInput
            aria-label={t("tickets.list.searchAria")}
            placeholder={t("tickets.list.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </InputGroup>
        <DataTableFacetedFilter
          label={t("tickets.fields.priority")}
          options={priorityOptions}
          values={priorities.map(String)}
          onValuesChange={(values) => updateSearchParams({ priority: values.length ? values.join(",") : null, page: null })}
        />
        <DataTableFacetedFilter
          label={t("tickets.fields.status")}
          options={statusOptions}
          values={statuses.map(String)}
          onValuesChange={(values) => updateSearchParams({ status: values.length ? values.join(",") : null, page: null })}
        />
        {hasFilters ? (
          <Button
            variant="ghost"
            onClick={() => {
              setSearch("");
              updateSearchParams({ q: null, priority: null, status: null, page: null });
            }}
          >
            <Icon data-icon="inline-start" icon="tabler:filter-x" />
            {t("tickets.actions.clearFilters")}
          </Button>
        ) : null}
      </DataTableToolbar>

      <section className="flex min-h-72 min-w-0 flex-1 flex-col">
        <DataTableRequestBoundary
          empty={{
            icon: <Icon className="size-5" icon="tabler:ticket-off" />,
            title: hasFilters || selectedTab.key !== "all" ? t("tickets.list.emptyFilteredTitle") : t("tickets.list.emptyTitle"),
            description: hasFilters || selectedTab.key !== "all" ? t("tickets.list.emptyFilteredDescription") : t("tickets.list.emptyDescription"),
          }}
          loadingLabel={t("tickets.list.loading")}
          query={query}
        >
          {(data) => (
            <EmbeddedTicketListTable
              key={`${selectedTab.key}|${page}|${appliedSearch}|${priorities.join(",")}|${statuses.join(",")}|${apiSorting}`}
              rows={data?.items ?? []}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          )}
        </DataTableRequestBoundary>
      </section>

      {!query.isPending && totalCount > 0 ? (
        <DataTablePagination
          className="pt-0"
          page={page}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={(nextPage) => updateSearchParams({ page: nextPage <= 1 ? null : String(nextPage) })}
        />
      ) : null}
    </main>
  );
}
